import { EditOutlined } from "@ant-design/icons";
import { Button, Col, message, Row, Table, TableProps, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useGetConfig } from "../../../api/v1";
import { SelectOption } from "../../../libs/types";
import { convertToSnakeCase, convertToTitleCase, findSelectOption, getSelectOptionValue } from "../../../libs/utils";
import { Popup } from "../../atoms/Popup";
import SectionHeader from "../../molecules/SectionHeader";
import { patchOrganization } from "./api/organizations";
import { PaymentTypeForm } from "./PaymentTypeForm";
import {
  leaseTypeOptions,
  patternOptions,
  paymentModeOptions,
  paymentTypeOptions,
  usageTypeOptions,
} from "./selectOptions";
import { defaultColumns } from "./tableColumns";
import { PaymentTypeFormData, PaymentTypePayload, PaymentTypeRecord } from "./types";
import { useOrganizations } from "./useOrganizations";
import { usePaymentsTypes } from "./usePaymentsTypes";

export const PaymentTypesModule = () => {
  const [modalMode, setModalMode] = useState<null | "add" | "edit">(null);
  const [editingRecord, setEditingRecord] = useState<PaymentTypeRecord>(null);

  const { orgId, organizations, organizationOptions, changeSelectedOrgId } = useOrganizations();
  const { paymentTypes, refetchPaymentTypes } = usePaymentsTypes();

  const methods = useForm<PaymentTypeFormData>();

  const watchedOrganization = useWatch({ name: "organization", control: methods.control });
  const watchedUsageType = useWatch({ name: "usageType", control: methods.control });

  const { config } = useGetConfig({
    params: {
      key: "masterPaymentTypeAttributes",
      orgId: watchedOrganization?.value,
      usageType: watchedUsageType?.value,
    },
    isEnabled: Boolean(watchedOrganization?.value && watchedUsageType?.value),
  });

  const descriptionOptions: SelectOption[] = useMemo(() => {
    return Object.entries(config?.[0]?.masterPaymentTypeAttributes ?? {}).map(([value, label]: [string, string]) => ({
      value,
      label,
    }));
  }, [config]);

  useEffect(() => {
    if (!Array.isArray(descriptionOptions) || descriptionOptions?.length === 0 || !editingRecord?.type) return;
    methods.setValue("description", findSelectOption(descriptionOptions, editingRecord.type));
  }, [descriptionOptions, editingRecord?.type]);

  const columns: TableProps<PaymentTypeRecord>["columns"] = [
    ...defaultColumns,
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: unknown, record: PaymentTypeRecord) => (
        <Button type="link" key={`edit-btn-${record.uid}`} onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  const resetPaymentForm = () => {
    const defaultValues: PaymentTypeFormData = {
      organization: null,
      description: null,
      usageType: null,
      vatInPercentage: 0,
      modeOfPayment: null,
      paymentType: null,
      defaultAmount: 0,
      leaseType: null,
      pattern: null,
      isUpfrontPayment: null,
      isWriteOff: null,
    };

    setModalMode(null);
    setEditingRecord(null);
    methods.reset(defaultValues);
  };

  const handleAdd = () => {
    resetPaymentForm();
    setModalMode("add");
  };

  const handleEdit = (record: PaymentTypeRecord) => {
    resetPaymentForm();

    setEditingRecord(record);
    setModalMode("edit");

    const defaultValues: PaymentTypeFormData = {
      organization: findSelectOption(organizationOptions, orgId),
      usageType: findSelectOption(usageTypeOptions, record.usageType),
      vatInPercentage: record.vatInPercentage || 0,
      modeOfPayment: findSelectOption(paymentModeOptions, record.modeOfPayment),
      paymentType: findSelectOption(paymentTypeOptions, record.paymentType),
      defaultAmount: record.defaultAmount || 0,
      leaseType: findSelectOption(leaseTypeOptions, record.leaseType),
      pattern: findSelectOption(patternOptions, record.pattern),
      isUpfrontPayment: record.isUpfrontPayment === "yes",
      isWriteOff: record.isWriteOff === "yes",
    };

    methods.reset(defaultValues);
  };

  const handleCancel = () => resetPaymentForm();

  const handleSubmit = async (formData: PaymentTypeFormData) => {
    try {
      const { organization, isUpfrontPayment, isWriteOff, ...restFormData } = formData;

      const record: PaymentTypeRecord = {
        uid: editingRecord?.uid,
        description: convertToTitleCase(restFormData.description?.label),
        type: convertToSnakeCase(restFormData.description?.value),
        usageType: getSelectOptionValue(restFormData.usageType),
        vatInPercentage: Number(restFormData.vatInPercentage),
        modeOfPayment: getSelectOptionValue(restFormData.modeOfPayment),
        paymentType: getSelectOptionValue(restFormData.paymentType),
        defaultAmount: Number(restFormData.defaultAmount),
        leaseType: getSelectOptionValue(restFormData.leaseType),
        pattern: getSelectOptionValue(restFormData.pattern),
        isUpfrontPayment: isUpfrontPayment ? "yes" : "no",
        isWriteOff: isWriteOff ? "yes" : "no",
      };

      const updatedList = editingRecord?.uid
        ? paymentTypes.map((p) => (p.uid === editingRecord.uid ? record : p))
        : [...paymentTypes, record];

      const payload: PaymentTypePayload[] = updatedList.map(({ uid, propertyLabels, ...rest }) => rest);

      const response = await patchOrganization({
        orgId: getSelectOptionValue(organization),
        data: { masterPaymentTypes: payload },
      });

      if (response?.success) {
        message.success("Updated successfully");
        resetPaymentForm();

        await refetchPaymentTypes();
      } else {
        message.error(response?.message ?? "Unable to update");
      }
    } catch {
      message.error("Something went wrong");
    }
  };

  const popupConfigs = {
    add: {
      title: <Typography.Title level={3}>Add Payment Type</Typography.Title>,
      content: (
        <PaymentTypeForm
          formType="add"
          methods={methods}
          onSubmit={handleSubmit}
          descriptionOptions={descriptionOptions}
        />
      ),
      width: 650,
    },
    edit: {
      title: <Typography.Title level={3}>Edit Payment Type</Typography.Title>,
      content: (
        <PaymentTypeForm
          formType="edit"
          methods={methods}
          onSubmit={handleSubmit}
          descriptionOptions={descriptionOptions}
        />
      ),
      width: 650,
    },
  } as const;

  return (
    <>
      <SectionHeader
        heading="Payment Types"
        addNewAction={handleAdd}
        organizationList={organizations}
        setOrgId={changeSelectedOrgId}
        orgFilterInitialValue={{ value: orgId }}
      />

      <Row gutter={20}>
        <Col span={24}>
          <Table
            rowKey={(record) => record.uid}
            className="custom-table org_table_wrap"
            columns={columns}
            dataSource={paymentTypes}
          />
        </Col>
      </Row>
      <Popup
        closable
        visibility={Boolean(modalMode)}
        title={popupConfigs[modalMode]?.title}
        content={popupConfigs[modalMode]?.content}
        width={popupConfigs[modalMode]?.width}
        onCancel={handleCancel}
      />
    </>
  );
};
